import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FileText, LayoutGrid, Plus, Sparkles } from "lucide-react";
import { api, MODULES } from "@/lib/api";
import { Price } from "@/lib/currency";

const path = (id) => MODULES.find((m) => m.id === id)?.path;

export function RelatedReports({ product }) {
  const { data } = useQuery({ queryKey: ["rel-reports", product], queryFn: async () => (await api.get("/related/reports", { params: { product } })).data, enabled: !!product });
  if (!data?.reports.length) return null;
  return (
    <section data-testid="related-reports" className="fade-up">
      <p className="font-mono text-[11px] uppercase tracking-widest text-[#03AB9E] mb-3">Related Special Reports{data.family ? ` · ${data.family}` : ""}</p>
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
        {data.reports.map((r) => (
          <Link key={r.id} to={`/reports/${r.id}`} data-testid={`related-report-${r.id}`} className="group bg-[#0F172A]/80 border border-slate-800 rounded-lg p-4 hover:border-[#1074BA] transition-colors">
            <FileText className="h-4 w-4 text-[#1074BA] group-hover:text-[#F58B23]" />
            <p className="text-sm font-medium mt-2 line-clamp-2">{r.title}</p>
            <p className="text-xs text-slate-500 mt-1 flex justify-between"><span>{r.category}</span><Price value={r.price} /></p>
          </Link>
        ))}
      </div>
    </section>
  );
}

export function RelatedDashboards({ reportId }) {
  const { data = [] } = useQuery({ queryKey: ["rel-dash", reportId], queryFn: async () => (await api.get("/related/dashboards", { params: { report_id: reportId } })).data });
  if (!data.length) return null;
  return (
    <section data-testid="related-dashboards" className="mt-12">
      <p className="font-mono text-xs uppercase tracking-widest text-[#03AB9E] mb-3">Related dashboards</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {data.map((d) => (
          <div key={d.product} className="border border-slate-800 rounded-lg p-4 bg-[#0F172A]/70" data-testid={`related-dash-${d.product}`}>
            <p className="font-medium flex items-center gap-2"><LayoutGrid className="h-4 w-4 text-[#1074BA]" />{d.product}</p>
            <p className="text-[11px] text-slate-500 mt-0.5">{d.match === "product" ? "Covered in this report" : `Same family · ${d.family}`}</p>
            <div className="flex flex-wrap gap-1.5 mt-3">{d.modules.map((m) => (
              <Link key={m.id} to={`${path(m.id)}?product=${encodeURIComponent(d.product)}`} data-testid={`related-dash-${d.product}-${m.id}`} className="text-[11px] px-2 py-1 rounded border border-slate-700 hover:border-[#F58B23] text-slate-300">{m.label}</Link>
            ))}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function BundleSuggest({ subs, onAdd, count = 0, nextTier }) {
  const key = JSON.stringify(subs.map((s) => [s.module, s.products?.[0] || s.product]));
  const { data = [] } = useQuery({ queryKey: ["bundle", key], queryFn: async () => (await api.post("/related/bundle", { subs })).data, enabled: subs.length > 0 });
  if (!data.length) return null;
  return (
    <div className="border border-dashed border-[#F58B23]/40 rounded-lg p-4" data-testid="bundle-suggest">
      <p className="text-sm font-semibold flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#F58B23]" />Complete your bundle</p>
      {nextTier && count > 0 && <p className="text-xs text-slate-400 mt-0.5">Add {nextTier.count - count} more to unlock {nextTier.pct}% off the whole order.</p>}
      <div className="grid sm:grid-cols-2 gap-2 mt-3">
        {data.map((s) => (
          <button key={`${s.module}-${s.product}`} onClick={() => onAdd(s)} data-testid={`bundle-add-${s.module}-${s.product}`}
            className="flex items-center gap-2 text-left px-3 py-2 rounded-md border border-slate-700 hover:border-[#F58B23] transition-colors">
            <Plus className="h-3.5 w-3.5 text-[#F58B23] shrink-0" />
            <span className="min-w-0"><span className="block text-xs text-white truncate">{s.product} · {s.label}</span><span className="block text-[10px] text-slate-500 truncate">{s.reason}</span></span>
          </button>
        ))}
      </div>
    </div>
  );
}
